import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Users, Landmark, Building, Building2, MapPin, Camera } from "lucide-react";
import { getStats } from "@/lib/api";
import { T } from "@/context/LanguageContext";

const ICONS = { users: Users, landmark: Landmark, building: Building, buildings: Building2, "map-pin": MapPin, camera: Camera };

function parseValue(v) {
  const m = /^([\d,.]+)\s*([A-Za-z+]*)$/.exec(v);
  if (!m) return { num: null, suffix: v };
  return { num: parseFloat(m[1].replace(/,/g, "")), suffix: m[2] };
}

function Counter({ value }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const { num, suffix } = parseValue(value);
  const [display, setDisplay] = useState(num === null ? value : "0");

  useEffect(() => {
    if (!inView || num === null) return;
    let start;
    const dur = 1400;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      const cur = num * eased;
      const shown = num >= 100 ? Math.round(cur).toLocaleString() : cur.toFixed(0);
      setDisplay(shown + suffix);
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, num, suffix]);

  return <span ref={ref}>{num === null ? <T>{value}</T> : display}</span>;
}

export function StatsBar() {
  const [items, setItems] = useState([]);
  useEffect(() => { getStats().then((d) => setItems(d.headline)).catch(() => {}); }, []);

  return (
    <section className="border-y border-border bg-parchment/60">
      <div className="mx-auto grid max-w-[1500px] grid-cols-2 gap-y-8 px-4 py-10 md:grid-cols-3 lg:grid-cols-6">
        {items.map((it, i) => {
          const Icon = ICONS[it.icon] || MapPin;
          return (
            <motion.div
              key={it.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="flex items-center gap-3"
              data-testid={`stat-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <span className="text-oldglory-red"><Icon className="h-8 w-8" strokeWidth={1.5} /></span>
              <span>
                <span className="block font-display text-2xl font-black leading-none text-navy md:text-3xl"><Counter value={it.value} /></span>
                <span className="text-sm text-muted-foreground"><T>{it.label}</T></span>
              </span>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

export function Marquee() {
  const words = ["EXPLORE AMERICA", "UNDERSTAND AMERICA", "CONNECT WITH AMERICA"];
  const seq = [...words, ...words, ...words];
  return (
    <div className="overflow-hidden border-y border-navy/10 bg-navy py-4">
      <div className="flex w-max animate-marquee">
        {seq.map((w, i) => (
          <span key={i} className="flex items-center">
            <span className="px-6 font-display text-xl font-semibold tracking-wide text-white/90 md:text-2xl">{w}</span>
            <span className="text-oldglory-red">★</span>
          </span>
        ))}
      </div>
    </div>
  );
}
